import { z } from "zod";

const NonEmptyString = z.string().trim().min(1);

export const HelpdeskConditionOperator = z.enum([
  "is",
  "is_one_of",
  "is_empty",
  "is_not_empty",
  "equals",
  "not_equals",
  "greater_than",
  "greater_than_or_equal",
  "less_than",
  "less_than_or_equal",
  "between",
  "before",
  "on_or_before",
  "after",
  "on_or_after",
  "is_checked",
  "is_unchecked",
  "has_items",
  "has_no_items",
]);
export type HelpdeskConditionOperator = z.infer<typeof HelpdeskConditionOperator>;

export const HelpdeskGroupConditionQuantifier = z.enum(["any", "all", "none"]);
export type HelpdeskGroupConditionQuantifier = z.infer<typeof HelpdeskGroupConditionQuantifier>;

export const HelpdeskConditionPredicate = z
  .object({
    fieldId: z.string().uuid(),
    groupId: z.string().uuid().optional(),
    quantifier: HelpdeskGroupConditionQuantifier.optional(),
    operator: HelpdeskConditionOperator,
    values: z.array(z.union([NonEmptyString, z.number().finite()])).default([]),
  })
  .superRefine((predicate, ctx) => {
    if (predicate.operator === "is" && predicate.values.length !== 1) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "operator 'is' requires exactly one value", path: ["values"] });
    if (predicate.operator === "is_one_of" && predicate.values.length < 1) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "operator 'is_one_of' requires at least one value", path: ["values"] });
    if ((predicate.operator === "is_empty" || predicate.operator === "is_not_empty") && predicate.values.length !== 0)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "empty operators take no values", path: ["values"] });
    if ((predicate.operator === "has_items" || predicate.operator === "has_no_items") && predicate.values.length !== 0)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "group presence operators take no values", path: ["values"] });
    if ((predicate.groupId === undefined) !== (predicate.quantifier === undefined)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "group targets require a quantifier", path: ["groupId"] });
    if (new Set(predicate.values).size !== predicate.values.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "values must be unique", path: ["values"] });
  });
export type HelpdeskConditionPredicate = z.infer<typeof HelpdeskConditionPredicate>;

export const HelpdeskConditionGroup = z.object({ predicates: z.array(HelpdeskConditionPredicate).min(1) });
export type HelpdeskConditionGroup = z.infer<typeof HelpdeskConditionGroup>;

export const HelpdeskDestinationCondition = z.object({
  matchAnyOf: z.array(HelpdeskConditionGroup),
  excludeAnyOf: z.array(HelpdeskConditionGroup),
});
export type HelpdeskDestinationCondition = z.infer<typeof HelpdeskDestinationCondition>;
